/**
 * rexSynthesis — classement des REX par cohorte + anonymisation pour le
 * document de synthèse (demande admin : « classer les REX par cohortes,
 * créer un document de synthèse des REX anonymisés d'une cohorte ou selon
 * une sélection manuelle »).
 */
export const REX_FIELDS = [
  { id: 'appreciated', label: "Ce que j'ai apprécié" },
  { id: 'regrets', label: 'Ce que je regrette' },
  { id: 'improvements', label: "Les axes d'amélioration" },
  { id: 'overall', label: 'Ressenti global' },
];

export const NO_COHORT_ID = '__none__';

export function cohortLabel(cohort) {
  if (!cohort) return 'Sans cohorte';
  const m = cohort.master || {};
  return `${m.holder_name || 'Responsable'}${m.establishment ? ` — ${m.establishment}` : ''}`;
}

/** Regroupe les REX par `master_license_id` → [{ id, label, items, cohort }]. */
export function groupRexByCohort(items = [], cohorts = []) {
  const byMaster = new Map(cohorts.map((c) => [c.master?.id, c]));
  const groups = new Map();
  items.forEach((rex) => {
    const key = rex.master_license_id || NO_COHORT_ID;
    if (!groups.has(key)) {
      const cohort = byMaster.get(key) || null;
      groups.set(key, { id: key, label: key === NO_COHORT_ID ? 'Sans cohorte' : (cohort ? cohortLabel(cohort) : 'Cohorte inconnue'), cohort, items: [] });
    }
    groups.get(key).items.push(rex);
  });
  return [...groups.values()].sort((a, b) => {
    if (a.id === NO_COHORT_ID) return 1;
    if (b.id === NO_COHORT_ID) return -1;
    return a.label.localeCompare(b.label, 'fr');
  });
}

function escapeRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

/** Remplace le nom de l'apprenant (et ses composantes ≥ 3 lettres) dans un texte. */
export function scrubName(text, holderName) {
  let out = String(text || '');
  const tokens = String(holderName || '').split(/[\s\-]+/).filter((t) => t.length >= 3);
  if (holderName && holderName.trim()) out = out.replace(new RegExp(escapeRe(holderName.trim()), 'gi'), '[apprenant]');
  tokens.forEach((tok) => { out = out.replace(new RegExp(`\\b${escapeRe(tok)}\\b`, 'gi'), '[apprenant]'); });
  return out.replace(/[A-Z0-9]{6}-[A-Z0-9]{6}-[A-Z0-9]{6}-[A-Z0-9]{6}/g, '[clé]');
}

/** Anonymise une liste de REX : « Apprenant n°1… », aucune clé ni nom. */
export function anonymizeRex(items = []) {
  return items.map((rex, i) => ({
    alias: `Apprenant n°${i + 1}`,
    source: rex.source,
    created_at: rex.created_at,
    has_video: !!rex.video_token,
    ...Object.fromEntries(REX_FIELDS.map((f) => [f.id, scrubName(rex[f.id], rex.holder_name).trim()])),
  }));
}

export function rexSynthesisStats(items = []) {
  const dates = items.map((r) => new Date(r.created_at).getTime()).filter(Number.isFinite);
  const filled = (id) => items.filter((r) => (r[id] || '').trim()).length;
  return {
    total: items.length,
    withVideo: items.filter((r) => r.video_token || r.has_video).length,
    from: dates.length ? new Date(Math.min(...dates)) : null,
    to: dates.length ? new Date(Math.max(...dates)) : null,
    bySource: items.reduce((acc, r) => { acc[r.source || 'expiring'] = (acc[r.source || 'expiring'] || 0) + 1; return acc; }, {}),
    filledByField: Object.fromEntries(REX_FIELDS.map((f) => [f.id, filled(f.id)])),
  };
}
